import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Spinner,
} from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ArrowLeft, CalendarClock, X } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import {
  BookingConflictError,
  cancelBooking,
  createBooking,
  getResource,
  listBookings,
  type BookingConflict,
  type ResourceBooking,
} from '../../lib/resources-api'

function fmtRange(startAt: string, endAt: string): string {
  const start = new Date(startAt)
  const end = new Date(endAt)
  const sameDay = start.toDateString() === end.toDateString()
  const dateFmt = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
  const timeFmt = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' })
  if (sameDay) {
    return `${dateFmt.format(start)} · ${timeFmt.format(start)} – ${timeFmt.format(end)}`
  }
  return `${dateFmt.format(start)} ${timeFmt.format(start)} – ${dateFmt.format(end)} ${timeFmt.format(end)}`
}

const statusVariant: Record<string, 'success' | 'gold' | 'neutral'> = {
  confirmed: 'success',
  tentative: 'gold',
  canceled: 'neutral',
}

function BookingForm({ resourceId }: { resourceId: string }) {
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [status, setStatus] = useState<'confirmed' | 'tentative'>('confirmed')
  const [conflicts, setConflicts] = useState<BookingConflict[]>([])

  const mutation = useMutation({
    mutationFn: () =>
      createBooking(resourceId, {
        title: title.trim(),
        startAt: new Date(start).toISOString(),
        endAt: new Date(end).toISOString(),
        status,
      }),
    onSuccess: () => {
      setTitle('')
      setStart('')
      setEnd('')
      setConflicts([])
      queryClient.invalidateQueries({ queryKey: ['resource-bookings', resourceId] })
    },
    onError: (err) => {
      setConflicts(err instanceof BookingConflictError ? err.conflicts : [])
    },
  })

  const invalidRange = Boolean(start && end && new Date(end) <= new Date(start))

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setConflicts([])
    if (!title.trim() || !start || !end || invalidRange) return
    mutation.mutate()
  }

  const conflictError = mutation.error instanceof BookingConflictError
  const otherError = mutation.isError && !conflictError

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="size-4 text-secondary" />
          Book this resource
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-1.5">
            <Label htmlFor="booking-title">Title</Label>
            <Input
              id="booking-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Youth group meeting"
              required
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="booking-start">Start</Label>
              <Input
                id="booking-start"
                type="datetime-local"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="booking-end">End</Label>
              <Input
                id="booking-end"
                type="datetime-local"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                aria-invalid={invalidRange}
                required
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="booking-status">Status</Label>
            <select
              id="booking-status"
              className="flex h-10 w-full rounded-md border border-border bg-bg-alt px-3 py-2 text-sm text-fg focus-visible:outline-none focus-visible:border-secondary focus-visible:ring-[3px] focus-visible:ring-ring"
              value={status}
              onChange={(e) => setStatus(e.target.value as 'confirmed' | 'tentative')}
            >
              <option value="confirmed">Confirmed</option>
              <option value="tentative">Tentative (hold)</option>
            </select>
          </div>

          {invalidRange && (
            <p className="text-sm text-danger" role="alert">
              End time must be after the start time.
            </p>
          )}

          {conflictError && (
            <div
              className="space-y-2 rounded-lg border border-warning/40 bg-warning/10 p-3"
              role="alert"
            >
              <div className="flex items-center gap-2 text-sm font-medium text-warning">
                <AlertTriangle className="size-4" />
                Double-booking detected
              </div>
              <p className="text-xs text-muted">
                This time conflicts with {conflicts.length} existing booking
                {conflicts.length === 1 ? '' : 's'}:
              </p>
              <ul className="space-y-1 text-xs text-muted">
                {conflicts.map((c) => (
                  <li key={c.bookingId} className="flex items-center justify-between gap-2">
                    <span className="truncate">{c.title}</span>
                    <span className="shrink-0 text-dim">{fmtRange(c.startAt, c.endAt)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {otherError && (
            <p className="text-sm text-danger" role="alert">
              {mutation.error instanceof Error
                ? mutation.error.message
                : 'Could not create booking.'}
            </p>
          )}

          <Button
            type="submit"
            disabled={mutation.isPending || !title.trim() || !start || !end || invalidRange}
          >
            {mutation.isPending ? 'Checking availability…' : 'Create booking'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

function BookingItem({ booking, resourceId }: { booking: ResourceBooking; resourceId: string }) {
  const queryClient = useQueryClient()
  const cancel = useMutation({
    mutationFn: () => cancelBooking(resourceId, booking.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['resource-bookings', resourceId] }),
  })

  return (
    <li className="flex items-center justify-between gap-3 px-5 py-3">
      <div className="min-w-0">
        <div className="truncate font-medium text-fg">{booking.title}</div>
        <div className="text-xs text-dim">{fmtRange(booking.startAt, booking.endAt)}</div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Badge variant={statusVariant[booking.status] ?? 'neutral'} className="capitalize">
          {booking.status}
        </Badge>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Cancel ${booking.title}`}
          disabled={cancel.isPending}
          onClick={() => cancel.mutate()}
        >
          <X />
        </Button>
      </div>
    </li>
  )
}

export function ResourceBookings() {
  const { id = '' } = useParams<{ id: string }>()

  const resourceQuery = useQuery({
    queryKey: ['resource', id],
    queryFn: () => getResource(id),
    enabled: Boolean(id),
  })

  const bookingsQuery = useQuery({
    queryKey: ['resource-bookings', id],
    queryFn: () => listBookings(id),
    enabled: Boolean(id),
  })

  const resource = resourceQuery.data

  return (
    <div className="space-y-6">
      <PageHeader
        title={resource?.name ?? 'Resource'}
        description={
          resource
            ? [resource.location, resource.siteName].filter(Boolean).join(' · ') ||
              'Manage bookings and check availability.'
            : 'Manage bookings and check availability.'
        }
        actions={
          <Button asChild variant="ghost" size="sm">
            <Link to="/app/resources">
              <ArrowLeft />
              All resources
            </Link>
          </Button>
        }
      />

      {resourceQuery.isError ? (
        <Card>
          <CardContent className="space-y-2 py-16 text-center">
            <p className="text-sm text-danger">Couldn’t load this resource.</p>
            <p className="text-xs text-dim">
              {resourceQuery.error instanceof Error
                ? resourceQuery.error.message
                : 'It may have been removed.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
          {/* Upcoming bookings */}
          <Card className="overflow-hidden p-0">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <h2 className="font-display text-lg font-semibold tracking-tight">Bookings</h2>
              {resource && !resource.isBookable && <Badge variant="neutral">Not bookable</Badge>}
            </div>
            {bookingsQuery.isLoading ? (
              <div className="flex items-center justify-center gap-2 py-16 text-muted">
                <Spinner /> Loading bookings…
              </div>
            ) : bookingsQuery.isError ? (
              <div className="py-16 text-center text-sm text-danger">Couldn’t load bookings.</div>
            ) : !bookingsQuery.data || bookingsQuery.data.length === 0 ? (
              <div className="py-16 text-center text-sm text-muted">
                No bookings yet. Reserve a time using the form.
              </div>
            ) : (
              <ul className="divide-y divide-border-soft">
                {bookingsQuery.data.map((b) => (
                  <BookingItem key={b.id} booking={b} resourceId={id} />
                ))}
              </ul>
            )}
          </Card>

          {/* Booking form */}
          {resource && resource.isBookable ? (
            <BookingForm resourceId={id} />
          ) : (
            <Card>
              <CardContent className="py-10 text-center text-sm text-muted">
                This resource is currently marked as not bookable.
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  )
}
